import { useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Global listener for plan-limit and subscription errors.
 * Mount once inside <Router> so useNavigate works.
 */
export default function PlanLimitListener() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    function onPlanLimit(e: Event) {
      const detail = (e as CustomEvent).detail;
      toast.error(detail?.message || t('plan_limit_reached', 'You\'ve reached a plan limit. Please upgrade.'), {
        duration: 6000,
        id: "plan-limit",
      });
      // Give the toast a moment, then offer upgrade
      toast(
        (toastObj) => (
          <span className="flex items-center gap-2">
            <button
              onClick={() => {
                toast.dismiss(toastObj.id);
                navigate("/billing");
              }}
              className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
            >
              {t('plan_upgrade_plan', 'Upgrade Plan')}
            </button>
          </span>
        ),
        { duration: 8000, id: "plan-limit-cta" }
      );
    }

    function onSubInactive(e: Event) {
      const detail = (e as CustomEvent).detail;
      toast.error(
        detail?.message || t('plan_sub_inactive', 'Your subscription is inactive. Please renew to continue.'),
        { duration: 8000, id: "sub-inactive" }
      );
    }

    window.addEventListener("plan-limit-reached", onPlanLimit);
    window.addEventListener("subscription-inactive", onSubInactive);
    return () => {
      window.removeEventListener("plan-limit-reached", onPlanLimit);
      window.removeEventListener("subscription-inactive", onSubInactive);
    };
  }, [navigate, t]);

  return null;
}
